import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { Item } from '@/types/models/Item';
import { ItemService } from '@/services';
import { logger } from '@/lib/logger';
import { CreateItemDTO } from '@/types';
import { STANDARD_UNITS } from '@/constants/units';
import { RootState } from '@/state/store';
import { selectCurrentStore, addStoreCategories } from './storesSlice';
import { normalizeStoreType } from '@/constants/storeTypes';

interface ItemsState {
  items: Item[];
  currentItem: Item | null;
  isLoading: boolean;
  isSingleItemLoading: boolean;
  error: string | null;
  storeId: string | null;
  isLoaded: boolean;
  searchQuery: string;
}

interface ItemActionPayload {
  storeId: string;
  id: string;
}

const initialState: ItemsState = {
  items: [],
  currentItem: null,
  isLoading: false,
  isSingleItemLoading: false,
  error: null,
  storeId: null,
  isLoaded: false,
  searchQuery: '',
};

export const fetchItems = createAsyncThunk(
  'items/fetchAll',
  async (storeId: string) => {
    logger.info('Fetching items', { storeId });
    return ItemService.getAll(storeId);
  }
);

export const fetchItemById = createAsyncThunk(
  'items/fetchById',
  async ({ storeId, id }: ItemActionPayload) => {
    logger.info('Fetching item by ID', { storeId, id });
    return ItemService.getById(storeId, id);
  }
);

export const createItem = createAsyncThunk(
  'items/create',
  async ({ storeId, data, enrich = false }: { storeId: string; data: any; enrich?: boolean }, { getState, dispatch }) => {
    logger.info('Creating item', { storeId, data: { ...data } });
    
    if (!data.name || !data.unit || !data.sell_price) {
      logger.error('Invalid item data', { storeId, data: { ...data } });
      throw new Error('Name, unit, and sell price are required');
    }
    
    if (data.sell_price <= 0) {
      logger.error('Invalid sell price', { storeId, sellPrice: data.sell_price });
      throw new Error('Sell price must be greater than 0');
    }
    
    // Get store info from Redux store to pass metadata
    const state = getState() as RootState;
    const currentStore = selectCurrentStore(state);

    // Verify we're using the correct store
    if (currentStore && currentStore.id !== storeId) {
      logger.warn('Current store ID mismatch', { 
        currentStoreId: currentStore.id, 
        requestedStoreId: storeId 
      });
    }

    if(enrich){
      // Use language_locale from data or currentStore or default to 'hindi'
      const languageLocale = data.language_locale || currentStore?.language_locale || 'hindi';
      const storeType = normalizeStoreType(currentStore?.store_type);
      const storeUnits = (currentStore?.units as any[]) || STANDARD_UNITS;
      
      // Get existing store categories to pass to LLM
      const existingStoreCategories = (currentStore?.categories || []) as string[];

      const enrichedData = await ItemService.getItemMetadata(data.name, storeType, storeUnits, existingStoreCategories, languageLocale, null);
      data.name_locale = enrichedData.item_name_local;
      data.keywords = enrichedData.keywords.join(',');
      data.language_locale = languageLocale;
      data.categories = enrichedData.categories || [];
    }
    
    const itemData: CreateItemDTO = {
      name: data.name,
      unit: data.unit,
      sell_price: data.sell_price,
      divisible_unit: data.divisible_unit,
      ...(data.description && { description: data.description }),
      ...(data.name_locale && { name_locale: data.name_locale }),
      ...(data.buy_price !== undefined && { buy_price: data.buy_price }),
      ...(data.max_retail_price !== undefined && { max_retail_price: data.max_retail_price }),
      ...(data.online_price !== undefined && { online_price: data.online_price }),
      ...(data.bar_codes && data.bar_codes.length > 0 && { bar_codes: data.bar_codes }),
      ...(data.short_codes && data.short_codes.length > 0 && { short_codes: data.short_codes }),
      ...(data.keywords && { keywords: data.keywords }),
      ...(data.language_locale && { language_locale: data.language_locale }),
      ...(data.categories && Array.isArray(data.categories) && data.categories.length > 0 && { categories: data.categories }),
      ...(data.serial_numbers && Array.isArray(data.serial_numbers) && data.serial_numbers.length > 0 && { serial_numbers: data.serial_numbers })
    };
    
    // Pass store metadata to avoid additional database call
    const storeMetadata = currentStore ? {
      ...(currentStore.online_store_enabled !== undefined && { online_store_enabled: currentStore.online_store_enabled }),
      ...(currentStore.online_price_enabled !== undefined && { online_price_enabled: currentStore.online_price_enabled })
    } : undefined;
    
    const createdItem = await ItemService.create(storeId, itemData, storeMetadata);
    
    // Update store's categories with newly found categories (if any)
    if (data.categories && Array.isArray(data.categories) && data.categories.length > 0) {
      try {
        // Get current store from Redux state to check existing categories
        const state = getState() as RootState;
        const currentStore = selectCurrentStore(state);
        
        // Get existing categories from store
        const existingCategories = (currentStore?.categories || []) as string[];
        
        // Filter out categories that already exist
        const newCategories = data.categories.filter(
          (cat: string) => !existingCategories.includes(cat)
        );
        
        // Only update store if there are new categories to add
        if (newCategories.length > 0) {
          // Update database and Redux store state via async thunk (fire and forget)
          dispatch(addStoreCategories({ storeId, newCategories }));
          
          logger.debug('Added new categories to store', { 
            storeId, 
            newCategories, 
            existingCount: existingCategories.length 
          });
        } else {
          logger.debug('All categories already exist in store', { 
            storeId, 
            categories: data.categories 
          });
        }
      } catch (err) {
        logger.warn('Failed to update store categories, continuing', { error: err, storeId, categories: data.categories });
      }
    }
    
    return createdItem;
  }
);

export const updateItem = createAsyncThunk(
  'items/update',
  async ({ storeId, id, data }: { storeId: string; id: string; data: any }) => {
    logger.info('Updating item', { storeId, id, data: { ...data } });
    
    if (data.sell_price !== undefined && data.sell_price <= 0) {
      logger.error('Invalid sell price in update', { 
        storeId, 
        id, 
        sellPrice: data.sell_price 
      });
      throw new Error('Sell price must be greater than 0');
    }
    
    const itemData: Parameters<typeof ItemService.update>[2] = {
      ...(data.name !== undefined && { name: data.name }),
      ...(data.name_locale !== undefined && { name_locale: data.name_locale }),
      ...(data.description !== undefined && { description: data.description || '' }),
      ...(data.unit !== undefined && { unit: data.unit }),
      ...(data.sell_price !== undefined && { sell_price: data.sell_price }),
      ...(data.buy_price !== undefined && { buy_price: data.buy_price }),
      ...(data.max_retail_price !== undefined && { max_retail_price: data.max_retail_price }),
      ...(data.online_price !== undefined && { online_price: data.online_price }),
      ...(data.divisible_unit !== undefined && { divisible_unit: data.divisible_unit }),
      ...(data.bar_codes !== undefined && { bar_codes: data.bar_codes }),
      ...(data.short_codes !== undefined && { short_codes: data.short_codes }),
      ...(data.keywords !== undefined && { keywords: data.keywords }),
      ...(data.categories !== undefined && { categories: data.categories }),
      ...(data.serial_numbers !== undefined && { serial_numbers: data.serial_numbers }),
      ...(data.available_quantity !== undefined && { available_quantity: data.available_quantity }),
      ...(data.is_out_of_stock !== undefined && { is_out_of_stock: data.is_out_of_stock }),
      ...(data.is_low_in_stock !== undefined && { is_low_in_stock: data.is_low_in_stock }),
      ...(data.updated_via_quick_add !== undefined && { updated_via_quick_add: data.updated_via_quick_add }),
      ...(data.language_locale !== undefined && { language_locale: data.language_locale })
    };
    
    return ItemService.update(storeId, id, itemData);
  }
);

export const deleteItem = createAsyncThunk(
  'items/delete',
  async ({ storeId, id }: ItemActionPayload) => {
    logger.info('Soft deleting item', { storeId, id });
    await ItemService.delete(storeId, id);
    return id;
  }
);

export const bulkUpdateStockStatus = createAsyncThunk(
  'items/bulkUpdateStockStatus',
  async ({ 
    storeId, 
    itemIds, 
    isOutOfStock 
  }: { 
    storeId: string; 
    itemIds: string[]; 
    isOutOfStock: boolean; 
  }) => {
    logger.info('Bulk updating stock status', { storeId, itemIds, isOutOfStock });
    return ItemService.bulkUpdateStockStatus(storeId, itemIds, isOutOfStock);
  }
);

export const bulkUpdateLowStockStatus = createAsyncThunk(
  'items/bulkUpdateLowStockStatus',
  async ({ 
    storeId, 
    itemIds, 
    isLowInStock 
  }: { 
    storeId: string; 
    itemIds: string[]; 
    isLowInStock: boolean; 
  }) => {
    logger.info('Bulk updating low stock status', { storeId, itemIds, isLowInStock });
    return ItemService.bulkUpdateLowStockStatus(storeId, itemIds, isLowInStock);
  }
);

export const deductStockForInvoice = createAsyncThunk(
  'items/deductStockForInvoice',
  async ({ 
    storeId, 
    lineItems 
  }: { 
    storeId: string; 
    lineItems: Array<{ item_id: string; quantity: number }>; 
  }) => {
    logger.info('Deducting stock for invoice', { storeId, lineItemsCount: lineItems.length });
    
    // Deduct stock and get updated field data
    const updatedFields = await ItemService.deductStockForInvoice(storeId, lineItems);
    
    return updatedFields;
  }
);

const itemsSlice = createSlice({
  name: 'items',
  initialState,
  reducers: {
    clearCurrentItem: (state) => {
      state.currentItem = null;
    },
    clearItems: (state) => {
      state.items = [];
      state.currentItem = null;
      state.storeId = null;
      state.isLoaded = false;
      state.searchQuery = '';
    },
    addItem: (state, action: PayloadAction<Item>) => {
      state.items.unshift(action.payload);
    },
    setSearchQuery: (state, action: PayloadAction<string>) => {
      state.searchQuery = action.payload;
    },
  },
  extraReducers: (builder) => {
    // Fetch All Items
    builder
      .addCase(fetchItems.pending, (state, action) => {
        state.isLoading = true;
        state.error = null;
        state.storeId = action.meta.arg;
      })
      .addCase(fetchItems.fulfilled, (state, action) => {
        state.items = action.payload;
        state.isLoading = false;
        state.isLoaded = true;
      })
      .addCase(fetchItems.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.error.message || 'Failed to fetch items';
        state.isLoaded = false;
        logger.error('Failed to fetch items', { error: action.error });
      });

    // Fetch Item by ID
    builder
      .addCase(fetchItemById.pending, (state) => {
        state.isSingleItemLoading = true;
        state.error = null;
      })
      .addCase(fetchItemById.fulfilled, (state, action: PayloadAction<Item>) => {
        state.isSingleItemLoading = false;
        state.currentItem = action.payload;
      })
      .addCase(fetchItemById.rejected, (state, action) => {
        state.isSingleItemLoading = false;
        state.error = action.error.message || 'Failed to fetch item';
        logger.error('Failed to fetch item by ID', { error: action.error });
      });

    // Create Item
    builder
      .addCase(createItem.pending, (state) => {
        state.isSingleItemLoading = true;
        state.error = null;
      })
      .addCase(createItem.fulfilled, (state, action: PayloadAction<Item>) => {
        state.isSingleItemLoading = false;
        state.items.unshift(action.payload);
      })
      .addCase(createItem.rejected, (state, action) => {
        state.isSingleItemLoading = false;
        state.error = action.error.message || 'Failed to create item';
        logger.error('Failed to create item', { error: action.error });
      });

    // Update Item
    builder
      .addCase(updateItem.pending, (state) => {
        state.isSingleItemLoading = true;
        state.error = null;
      })
      .addCase(updateItem.fulfilled, (state, action: PayloadAction<Item>) => {
        state.isSingleItemLoading = false;
        const index = state.items.findIndex(item => item.id === action.payload.id);
        if (index !== -1) {
          state.items[index] = action.payload;
        }
        state.currentItem = action.payload;
      })
      .addCase(updateItem.rejected, (state, action) => {
        state.isSingleItemLoading = false;
        state.error = action.error.message || 'Failed to update item';
        logger.error('Failed to update item', { error: action.error });
      });

    // Delete Item
    builder
      .addCase(deleteItem.pending, (state) => {
        state.isSingleItemLoading = true;
        state.error = null;
      })
      .addCase(deleteItem.fulfilled, (state, action: PayloadAction<string>) => {
        state.isSingleItemLoading = false;
        state.items = state.items.filter(item => item.id !== action.payload);
        if (state.currentItem?.id === action.payload) {
          state.currentItem = null;
        }
      })
      .addCase(deleteItem.rejected, (state, action) => {
        state.isSingleItemLoading = false;
        state.error = action.error.message || 'Failed to delete item';
        logger.error('Failed to delete item', { error: action.error });
      });

    builder
      .addCase(bulkUpdateStockStatus.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(bulkUpdateStockStatus.fulfilled, (state, action: PayloadAction<Item[]>) => {
        state.isLoading = false;
        action.payload.forEach(updatedItem => {
          const index = state.items.findIndex(item => item.id === updatedItem.id);
          if (index !== -1) {
            state.items[index] = updatedItem;
          }
        });
      })
      .addCase(bulkUpdateStockStatus.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.error.message || 'Failed to update stock status';
        logger.error('Failed to bulk update stock status', { error: action.error });
      });

    // Bulk Update Low Stock Status
    builder
      .addCase(bulkUpdateLowStockStatus.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(bulkUpdateLowStockStatus.fulfilled, (state, action: PayloadAction<Item[]>) => {
        state.isLoading = false;
        action.payload.forEach(updatedItem => {
          const index = state.items.findIndex(item => item.id === updatedItem.id);
          if (index !== -1) {
            state.items[index] = updatedItem;
          }
        });
      })
      .addCase(bulkUpdateLowStockStatus.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.error.message || 'Failed to update low stock status';
        logger.error('Failed to bulk update low stock status', { error: action.error });
      });

    // Deduct Stock for Invoice
    builder
      .addCase(deductStockForInvoice.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(deductStockForInvoice.fulfilled, (state, action: PayloadAction<Array<{
        item_id: string;
        new_available_quantity: number;
        new_is_out_of_stock: boolean;
        new_updated_at: string;
      }>>) => {
        state.isLoading = false;
        // Update only the specific fields that changed
        action.payload.forEach(updatedField => {
          const index = state.items.findIndex(item => item.id === updatedField.item_id);
          if (index !== -1) {
            const item = state.items[index];
            if (item) {
              item.available_quantity = updatedField.new_available_quantity;
              item.is_out_of_stock = updatedField.new_is_out_of_stock;
              item.updated_at = updatedField.new_updated_at;
            }
          }
        });
        logger.info('Stock deducted successfully for invoice', { 
          itemsUpdated: action.payload.length 
        });
      })
      .addCase(deductStockForInvoice.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.error.message || 'Failed to deduct stock for invoice';
        logger.error('Failed to deduct stock for invoice', { error: action.error });
      });
  },
});

export const { clearCurrentItem, clearItems, addItem, setSearchQuery } = itemsSlice.actions;
export default itemsSlice.reducer;

// Selector for items
export const selectItems: (state: RootState) => Item[] = (state: RootState) => state.items.items as Item[];